import { inject, observer } from 'mobx-react';
import * as React from 'react';
import {
  Check,
  ChoiceGroup,
  Grid,
  GridColumn,
  IButtonProps,
  ICheckProps,
  IChoiceGroupProps,
  ISelectOption,
  ISelectorProps,
  ISelectProps,
  ITextFieldProps,
  Notice,
  Radio,
  Select,
  Selector,
  Separator,
  Text,
  TextField
} from 'telia-front-react';

import ButtonWithAnalytics from '../components/common/ButtonWithAnalytics';
import { ITranslateStore } from '../stores/TranslateStore';

export interface ITemplateBillingSettingsPanelProps {
  /**
   * Panel title.
   */
  title: string;
  /**
   * If first header should be larger.
   */
  hasMainHeader: boolean;
  /**
   * Bill data.
   */
  bill: ITemplateBillingSettingsPanelBillInfo;
  /**
   * Delivery options
   */
  deliveryOptions: ITemplateBillingSettingsPanelDeliveryOptions;

  recipientOptions?: ITemplateBillingSettingsPanelRecipientInfo;
  /**
   * Error notice content
   */
  errorNotice: React.ReactNode;
  translateStore?: ITranslateStore;
}

export interface ITemplateBillingSettingsPanelBillInfo {
  /**
   * Illustrative icon.
   */
  icon: string;
  /**
   * Title of service.
   */
  serviceTitle: string;

  label: string;
  /**
   * Number of bill.
   */
  billNumber: string;
  /**
   * Receiver of bill.
   */
  receiver?: IChoiceGroupProps;
  /**
   * Language of bill.
   */
  languageHeading: string;
  language: ISelectorProps;
  /**
   * Title of bill.
   */
  billTitle: ITextFieldProps;
}

export interface ITemplateBillingSettingsPanelDeliveryOptions {
  /**
   * Title of section.
   */
  title: string;
  /**
   * Description of section.
   */
  description: string;
  /**
   * Order label.
   */
  orderLabel: string;
  /**
   * Self service option check.
   */
  selfServiceOptionCheck: ICheckProps;
  /**
   * Bank option check.
   */
  bankOptionCheck: ICheckProps;
  /**
   * Bank option info.
   */
  bankOptionInfo: string;
  /**
   * Bank option account number textfield.
   */
  bankOptionAccountNumber: ITextFieldProps;
  /**
   * Bank option beneficiary name textfield.
   */
  bankOptionUserName: ITextFieldProps;
  /**
   * Bank option beneficiary registration code textfield.
   */
  bankOptionUserCode: ITextFieldProps;

  bankOptionAddress: ITextFieldProps;

  bankOptionShowCancelPostDelivery: boolean;

  bankOptionDropdown: ISelectProps;
  /**
   * Email option check.
   */
  emailOptionCheck: ICheckProps;

  emailOptionShowCancelPostDelivery: boolean;

  /**
   * Email address textfield.
   */
  emailAddresses: ITextFieldProps[];
  /**
   * Format of bill.
   */
  billFormat?: IChoiceGroupProps;
  /**
   * Add new email button
   */
  addEmailButton: IButtonProps;
  /**
   * Paper bill option check
   */
  paperBillOptionCheck: ICheckProps;
  /**
   * Paper bill option notice text.
   */
  paperBillNotice: string;
  /**
   * Address component provider.
   */
  paperBillAddressComponent?: React.ReactNode;

  cancelPostDeliveryNoticeText: string;

  cancelPostDeliveryCheck: ICheckProps;

  invoiceCentralOptionCheck: ICheckProps;

  invoiceCentralOptionInfo: string;

  invoiceCentralOptionShowCancelPostDelivery: boolean;

  invoiceCentralSupplier: IChoiceGroupProps;

  invoiceCentralOptionAddress: ITextFieldProps;

  invoiceCentralOptionDropdown: ISelectProps;

  invoiceCentralSupported: boolean;

  multipleDeliveryOptionsSupported: boolean;

  mobileOptionCheck: ICheckProps;

  mobileOptionShowCancelPostDelivery: boolean;

  mobileNumber: ITextFieldProps;
}

export interface ITemplateBillingSettingsPanelRecipientInfo {
  isSameAsCustomerChoice: IChoiceGroupProps;

  isSameAsCustomer: boolean;

  recipientName: ITextFieldProps;

  recipientAddressComponent: React.ReactNode;

  headingLabel: string;
}

@inject('translateStore')
@observer
export default class TemplateBillingSettingsPanel extends React.Component<ITemplateBillingSettingsPanelProps> {
  private renderLanguageOptions(language: ISelectorProps): JSX.Element {
    return (
      <React.Fragment>
        {language && (
          <>
            <Separator spacing="small" />
            <h2 className="text-bold h4">{this.props.bill.languageHeading}</h2>
            <Selector {...language} items={language.items} value={language.value} />
          </>
        )}
      </React.Fragment>
    );
  }

  private renderRecipientInfo(recipientInfo: ITemplateBillingSettingsPanelRecipientInfo) {
    return (
      <>
        <h2 className="text-bold h4">{this.props.recipientOptions.headingLabel}</h2>
        <ChoiceGroup
          {...recipientInfo.isSameAsCustomerChoice}
          options={recipientInfo.isSameAsCustomerChoice.options}
          value={recipientInfo.isSameAsCustomerChoice.value}
        />
        {!recipientInfo.isSameAsCustomer && TemplateBillingSettingsPanel.renderBillRecipientInfo(recipientInfo)}
        <Separator spacing="small" />
      </>
    );
  }

  private static renderBillRecipientInfo(recipientInfo: ITemplateBillingSettingsPanelRecipientInfo): JSX.Element {
    return (
      <>
        <Grid>
          <GridColumn width={['xs-12', 'md-8']}>
            <TextField key="user-name-textfield" {...recipientInfo.recipientName} />
          </GridColumn>
        </Grid>
        {recipientInfo.recipientAddressComponent}
        <Separator color="invisible" spacing="xxsmall" />
      </>
    );
  }

  private renderBillTitle(bill: ITemplateBillingSettingsPanelBillInfo) {
    return (
      <>
        {this.props.hasMainHeader ? (
          <h2 id="invoice-settings-panel-title" className="h3">
            {this.props.bill.label}
          </h2>
        ) : (
          <h2 id="invoice-settings-panel-title" className="text-bold h4">
            {this.props.bill.label}
          </h2>
        )}
        <Grid>
          <GridColumn width={['xs-12', 'md-8']}>
            {this.props.bill.billTitle ? <TextField {...bill.billTitle} /> : <div className="text-bold">{bill.serviceTitle}</div>}
          </GridColumn>
        </Grid>
        <Separator spacing="small" />
      </>
    );
  }

  private renderDeliveryOptions(delivery: ITemplateBillingSettingsPanelDeliveryOptions) {
    const multipleDeliveryOptionsSupported = this.props.deliveryOptions.multipleDeliveryOptionsSupported;
    const DeliveryOptionChoice = multipleDeliveryOptionsSupported ? Check : Radio;

    return (
      <React.Fragment>
        <h2 className="text-bold h4">{delivery.title}</h2>
        <Notice size="small">
          <div>{delivery.description}</div>
        </Notice>
        <Grid>
          {this.props.deliveryOptions.multipleDeliveryOptionsSupported ? null : (
            <GridColumn width={['xs-11']}>
              <Text>
                <p>{delivery.orderLabel}</p>
                <DeliveryOptionChoice {...delivery.selfServiceOptionCheck} className="text__margin" />
              </Text>
            </GridColumn>
          )}
          {multipleDeliveryOptionsSupported && (
            <GridColumn width={['xs-11']}>
              <DeliveryOptionChoice {...delivery.mobileOptionCheck} />
            </GridColumn>
          )}
          {delivery.mobileOptionCheck.checked && TemplateBillingSettingsPanel.renderMobileOption(delivery)}
          <GridColumn width={['xs-11']}>
            <DeliveryOptionChoice {...delivery.bankOptionCheck} />
          </GridColumn>
          {delivery.bankOptionCheck.checked
            ? multipleDeliveryOptionsSupported
              ? TemplateBillingSettingsPanel.renderBankMultipleOption(delivery)
              : TemplateBillingSettingsPanel.renderBankSingleOption(delivery)
            : null}
          <GridColumn width={['xs-11']}>
            <DeliveryOptionChoice {...delivery.emailOptionCheck} />
          </GridColumn>
          {delivery.emailOptionCheck.checked ? TemplateBillingSettingsPanel.renderEmailOption(delivery) : null}
          {delivery.invoiceCentralSupported ? (
            <React.Fragment>
              <GridColumn width={['xs-11']}>
                <DeliveryOptionChoice {...delivery.invoiceCentralOptionCheck} />
              </GridColumn>
              {delivery.invoiceCentralOptionCheck.checked
                ? multipleDeliveryOptionsSupported
                  ? TemplateBillingSettingsPanel.renderInvoiceCentralMultipleOption(delivery)
                  : TemplateBillingSettingsPanel.renderInvoiceCentralSingleOption(delivery)
                : null}
            </React.Fragment>
          ) : null}
          <GridColumn width={['xs-11']}>
            <DeliveryOptionChoice {...delivery.paperBillOptionCheck} />
          </GridColumn>
          {delivery.paperBillOptionCheck.checked ? TemplateBillingSettingsPanel.renderPaperBillOption(delivery) : null}
        </Grid>
      </React.Fragment>
    );
  }

  private static renderPaperBillOption(delivery: ITemplateBillingSettingsPanelDeliveryOptions): JSX.Element {
    return (
      <GridColumn width={['xs-11']} offset={['xs-1']}>
        <Grid horizontalGutter="none">
          <GridColumn width={['xs']}>
            <Text>
              <Notice>
                <div>{delivery.paperBillNotice}</div>
              </Notice>
              {delivery.paperBillAddressComponent}
            </Text>
          </GridColumn>
        </Grid>
      </GridColumn>
    );
  }

  private static renderEmailOption(delivery: ITemplateBillingSettingsPanelDeliveryOptions) {
    return (
      <GridColumn width={['xs-11']} offset={['xs-1']}>
        <Grid horizontalGutter="none">
          <GridColumn width={['xs-12', 'sm-9']}>
            <Text>
              {delivery.emailAddresses.map((emailAddress, index) => (
                <TextField key={index} {...emailAddress} />
              ))}
              {delivery.addEmailButton ? this.renderAddEmailButton(delivery) : null}
              {delivery.billFormat ? <ChoiceGroup {...delivery.billFormat} options={delivery.billFormat.options} value={delivery.billFormat.value} /> : null}
            </Text>
          </GridColumn>
          {delivery.emailOptionShowCancelPostDelivery && this.renderUncheckPostOption(delivery)}
        </Grid>
      </GridColumn>
    );
  }

  private static renderMobileOption(delivery: ITemplateBillingSettingsPanelDeliveryOptions) {
    return (
      <GridColumn width={['xs-11']} offset={['xs-1']}>
        <Grid horizontalGutter="none">
          <GridColumn width={['xs-12', 'sm-9']}>
            <Text>
              <TextField {...delivery.mobileNumber} />
            </Text>
          </GridColumn>
          {delivery.mobileOptionShowCancelPostDelivery && this.renderUncheckPostOption(delivery)}
        </Grid>
      </GridColumn>
    );
  }

  private static renderUncheckPostOption(delivery: ITemplateBillingSettingsPanelDeliveryOptions) {
    return (
      <GridColumn>
        <Notice size="small">{delivery.cancelPostDeliveryNoticeText}</Notice>
        <Separator spacing="xxsmall" color="invisible" />
        <Check {...delivery.cancelPostDeliveryCheck} onChange={(checked) => delivery.cancelPostDeliveryCheck.onChange(checked)} />
      </GridColumn>
    );
  }

  private static renderAddEmailButton(delivery: ITemplateBillingSettingsPanelDeliveryOptions): JSX.Element {
    return (
      <GridColumn width={['xs-12']}>
        <div className="text-center">
          <ButtonWithAnalytics
            gaEvent="CTA - add second email"
            buttonProps={{
              iconRight: '#arrow-down',
              size: 'small',
              type: 'primary',
              ...delivery.addEmailButton
            }}
          />
        </div>
      </GridColumn>
    );
  }

  private static renderBankSingleOption(delivery: ITemplateBillingSettingsPanelDeliveryOptions): JSX.Element {
    return (
      <GridColumn width={['xs-11']} offset={['xs-1']}>
        <Grid horizontalGutter="none">
          <GridColumn width={['xs-12', 'sm-9']}>
            <Text>
              <Notice size="small">
                <div>{delivery.bankOptionInfo}</div>
              </Notice>
              <TextField key="bank-user-name-textfield" {...delivery.bankOptionUserName} />
              <TextField {...delivery.bankOptionUserCode} />
              <TextField key="bank-account-number-textfield" {...delivery.bankOptionAccountNumber} />
            </Text>
          </GridColumn>
        </Grid>
      </GridColumn>
    );
  }

  private static renderBankMultipleOption(delivery: ITemplateBillingSettingsPanelDeliveryOptions): JSX.Element {
    return (
      <GridColumn width={['xs-11']} offset={['xs-1']}>
        <Grid horizontalGutter="none">
          <GridColumn width={['xs-12', 'sm-9']}>
            <Text>
              <Notice size="small">
                <div>{delivery.bankOptionInfo}</div>
              </Notice>
              <Select {...delivery.bankOptionDropdown} />
              {(delivery.bankOptionDropdown.value as ISelectOption).value != null && (
                <TextField key="bank-account-number-textfield" {...delivery.bankOptionAddress} />
              )}
            </Text>
          </GridColumn>
          {delivery.bankOptionShowCancelPostDelivery && this.renderUncheckPostOption(delivery)}
        </Grid>
      </GridColumn>
    );
  }

  private static renderInvoiceCentralMultipleOption(delivery: ITemplateBillingSettingsPanelDeliveryOptions): JSX.Element {
    return (
      <GridColumn width={['xs-11']} offset={['xs-1']}>
        <Grid horizontalGutter="none">
          <GridColumn width={['xs-12', 'sm-9']}>
            <Text>
              <Select {...delivery.invoiceCentralOptionDropdown} />
              {(delivery.invoiceCentralOptionDropdown.value as ISelectOption).value != null && (
                <TextField key="invoice-central-account-number-textfield" {...delivery.invoiceCentralOptionAddress} />
              )}
            </Text>
          </GridColumn>
          {delivery.invoiceCentralOptionShowCancelPostDelivery && this.renderUncheckPostOption(delivery)}
        </Grid>
      </GridColumn>
    );
  }

  private static renderInvoiceCentralSingleOption(delivery: ITemplateBillingSettingsPanelDeliveryOptions): JSX.Element {
    return (
      <GridColumn width={['xs-11']} offset={['xs-1']}>
        <Grid horizontalGutter="none">
          <GridColumn width={['xs-12', 'sm-9']}>
            <Text>
              <Notice size="small">
                <div>{delivery.invoiceCentralOptionInfo}</div>
              </Notice>
              <ChoiceGroup
                {...delivery.invoiceCentralSupplier}
                options={delivery.invoiceCentralSupplier.options}
                value={delivery.invoiceCentralSupplier.value}
              />
            </Text>
          </GridColumn>
        </Grid>
      </GridColumn>
    );
  }

  public render(): JSX.Element {
    return (
      <Text>
        <h1 className="sr-only">{this.props.translateStore.translate('invoices.invoice-settings-modal.title')}</h1>
        {this.props.errorNotice ? this.props.errorNotice : null}
        {this.renderBillTitle(this.props.bill)}
        {this.props.recipientOptions && this.renderRecipientInfo(this.props.recipientOptions)}
        {this.renderDeliveryOptions(this.props.deliveryOptions)}
        {this.renderLanguageOptions(this.props.bill.language)}
      </Text>
    );
  }
}
